import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import { MdInventory2, MdViewAgenda } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import { Field, FormHeader, GHOST_BTN, ProductPicker, STATUS_OPTIONS, Section, YELLOW_BTN } from './formShared';

const defaultFormValues = { title: '', layoutId: '', position: '', product_ids: [], status: 'active' };

const LayoutForm = ({ open, handleClose, handleSubmit, isEdit = false, editingLayout = null }) => {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [layoutOptions, setLayoutOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setErrors({});
    if (isEdit && editingLayout) {
      setFormValues({
        title: editingLayout.type || editingLayout.title || '',
        layoutId: editingLayout.layoutId || '',
        position: editingLayout.position?.toString() || '',
        product_ids: Array.isArray(editingLayout.product_ids)
          ? editingLayout.product_ids
          : Array.isArray(editingLayout.products)
            ? editingLayout.products.map((p) => p._id || p.id || p).filter(Boolean)
            : [],
        status: editingLayout.status || 'active',
      });
    } else {
      setFormValues(defaultFormValues);
    }
  }, [isEdit, editingLayout, open]);

  // the choice of row design the app can render
  useEffect(() => {
    const raw = sessionStorage.getItem('token');
    getData('homepage/layout/master/list', null, raw ? `Bearer ${raw}` : '')
      .then((res) => setLayoutOptions(res && Array.isArray(res.layout) ? res.layout.map((l) => ({ value: l._id, label: l.name })) : []))
      .catch(() => setLayoutOptions([]));
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!formValues.title.trim()) next.title = 'Give this section a title';
    if (!formValues.layoutId) next.layoutId = 'Choose a layout design';
    if (formValues.position === '' || Number(formValues.position) < 1) next.position = 'Enter a position of 1 or more';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await handleSubmit?.({ ...formValues, title: formValues.title.trim() });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <FormHeader
        title={isEdit ? 'Edit Layout Section' : 'Add Layout Section'}
        subtitle="A row of products shown on the app's home screen."
        onBack={handleClose}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Section title="Layout details" hint="Name, design and where it appears" accent="#0ea5e9" icon={MdViewAgenda}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Field label="Title" name="title" value={formValues.title} onChange={onChange} required error={!!errors.title} helperText={errors.title} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Layout design" name="layoutId" value={formValues.layoutId} onChange={onChange} required options={layoutOptions} error={!!errors.layoutId} helperText={errors.layoutId || (layoutOptions.length ? '' : 'No layout designs available')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Position" name="position" type="number" inputProps={{ min: 1 }} value={formValues.position} onChange={onChange} required error={!!errors.position} helperText={errors.position || 'Lower numbers appear higher on the screen'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Field label="Status" name="status" value={formValues.status} onChange={onChange} options={STATUS_OPTIONS} />
            </Grid>
          </Grid>
        </Section>

        <Section title="Products" hint="Tick the products to show in this section" accent="#f59e0b" icon={MdInventory2}>
          <ProductPicker
            selectedIds={formValues.product_ids}
            onChange={(ids) => setFormValues((prev) => ({ ...prev, product_ids: ids }))}
            known={editingLayout?.products || []}
          />
        </Section>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button variant="outlined" onClick={handleClose} sx={GHOST_BTN}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving} sx={YELLOW_BTN}>
            {saving ? <><CircularProgress size={18} sx={{ color: 'black', mr: 1 }} />Saving…</> : isEdit ? 'Update layout' : 'Create layout'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LayoutForm;
